import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { getBranding } from "../api/brandingApi";
import { ECOMMERCE_URL } from "../env";
import { ROLES_WITH_ECOMMERCE_LINK_ACCESS } from "../roles/permissions";

/**
 * "Links to anything else the user has access to" (owner-facing dashboard requirement).
 * `ecommerce-store` is a genuinely separate app/origin, so it's still an external redirect
 * authenticated via the SSO token handoff (`backend/app/services/sso_service.py`) rather
 * than a second login. **POS is no longer here** — as of the `pos-pwa` merge, "Point of
 * Sale" is a plain in-app link (`BASIC_NAV_ITEMS` in `roles/permissions.ts`, rendered by
 * `NavHeader` itself), not a cross-app SSO redirect, since `/pos/*` now lives in this same
 * app/origin.
 */
export function CrossAppLinks() {
  const { user, ensureFreshToken, initiateSso } = useAuth();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `ecommerce-store` is one shared deployment resolving the tenant from the
  // first URL path segment (§11 follow-up) — its `/sso` landing page lives
  // at `/{slug}/sso`, not the app root, so this link needs the tenant's own
  // slug before it can be built.
  const [ecommerceSlug, setEcommerceSlug] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !ROLES_WITH_ECOMMERCE_LINK_ACCESS.includes(user.role)) return;
    let cancelled = false;
    ensureFreshToken()
      .then((token) => getBranding(token))
      .then((branding) => {
        if (!cancelled) setEcommerceSlug(branding.slug);
      })
      .catch(() => {
        // Leave ecommerceSlug null — the Online Store link just stays hidden below.
      });
    return () => {
      cancelled = true;
    };
  }, [user, ensureFreshToken]);

  if (!user || !ROLES_WITH_ECOMMERCE_LINK_ACCESS.includes(user.role) || !ecommerceSlug) return null;

  async function openEcommerceStore() {
    setError(null);
    setPending(true);
    try {
      const code = await initiateSso("dashboard");
      // New tab, not same-tab navigation — switching apps shouldn't lose the
      // dashboard the owner was working in.
      window.open(`${ECOMMERCE_URL}/${ecommerceSlug}/sso?code=${encodeURIComponent(code)}`, "_blank", "noopener,noreferrer");
    } catch {
      setError("Couldn't open the store right now — try again.");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="sl-cross-app-links">
      <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.6)", marginBottom: "0.25rem" }}>Other apps</div>
      <button
        type="button"
        className="sl-button sl-button--ghost sl-button--md"
        style={{ width: "100%", justifyContent: "flex-start" }}
        disabled={pending}
        onClick={() => void openEcommerceStore()}
      >
        {pending ? "Opening…" : "Online Store"}
      </button>
      {error ? (
        <p className="banner banner--error" role="alert" style={{ fontSize: "0.8rem" }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
